import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { authAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { PasswordInput } from '@/components/ui/password-input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, AlertCircle, Mail, MessageSquare, ShieldCheck } from 'lucide-react';

const LOGO_LIGHT_BG = "https://customer-assets-v7afamib.emergentagent.net/job_24da4b5c-f7b8-4285-a82c-9685a2a83611/artifacts/xys5iiar_1%20Clear%20copy.png";
const LOGIN_BG = "https://images.unsplash.com/photo-1766050591098-93cb26354689?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNDR8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBvZmZpY2UlMjBidWlsZGluZ3MlMjBza3lsaW5lJTIwYnVzaW5lc3MlMjBkaXN0cmljdCUyMGxpZ2h0fGVufDB8fHx8MTc3MjY4NTgxOXww&ixlib=rb-4.1.0&q=85";

export default function LoginPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login, completeTwoFactor, activate, isAuthenticated } = useAuth();
  
  const [mode, setMode] = useState('login'); // 'login' or 'activate'
  const [invitationData, setInvitationData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  // Two-factor challenge state (null when not in a 2FA flow).
  const [twoFA, setTwoFA] = useState(null);
  const [twoFACode, setTwoFACode] = useState('');
  const [rememberDevice, setRememberDevice] = useState(true);
  
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
  });

  // Check for invitation token
  useEffect(() => {
    const token = searchParams.get('token');
    if (token) {
      verifyInvitation(token);
    }
  }, [searchParams]);

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard');
    }
  }, [isAuthenticated, navigate]);

  const verifyInvitation = async (token) => {
    try {
      const response = await authAPI.verifyInvitation(token);
      setInvitationData({ ...response.data, token });
      setFormData(prev => ({ ...prev, email: response.data.email }));
      setMode('activate');
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid invitation link');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (mode === 'activate') {
        if (formData.password !== formData.confirmPassword) {
          setError('Passwords do not match');
          setLoading(false);
          return;
        }
        if (formData.password.length < 6) {
          setError('Password must be at least 6 characters');
          setLoading(false);
          return;
        }
        await activate(invitationData.token, formData.email, formData.password);
      } else {
        const res = await login(formData.email, formData.password);
        if (res && res.twoFactorRequired) {
          setTwoFA({ ...res, stage: 'choose', destination: null, channel: null });
          setLoading(false);
          return;
        }
      }
      navigate('/dashboard');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const chooseChannel = async (channel) => {
    setError('');
    setLoading(true);
    try {
      const { data } = await authAPI.twoFactorSend(twoFA.pending_token, channel);
      setTwoFA((p) => ({ ...p, stage: 'code', channel: data.channel, destination: data.destination_masked }));
    } catch (err) {
      setError(err.response?.data?.detail || 'Could not send the code');
    } finally {
      setLoading(false);
    }
  };

  const verifyTwoFactor = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const { data } = await authAPI.twoFactorVerify(twoFA.pending_token, twoFACode.trim(), rememberDevice);
      completeTwoFactor(data);
      navigate('/dashboard');
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid code');
    } finally {
      setLoading(false);
    }
  };

  const resetTwoFactor = () => {
    setTwoFA(null);
    setTwoFACode('');
    setError('');
  };

  const handleChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  return (
    <div className="login-container" data-testid="login-page">
      {/* Left side - Image */}
      <div 
        className="login-image hidden lg:block"
        style={{ backgroundImage: `url(${LOGIN_BG})` }}
      >
        <div className="login-image-content">
          <div className="flex-1">
            <h2 className="text-3xl font-heading font-semibold mb-4 mt-8">
              KR Agency Hub
            </h2>
            <p className="text-slate-300 text-lg max-w-md">
              Streamline your agency contracting workflow with our comprehensive management platform.
            </p>
          </div>
          <div className="text-sm text-slate-400">
            © 2026 KR Enterprise Group. All rights reserved.
          </div>
        </div>
      </div>
      
      {/* Right side - Form */}
      <div className="login-form-container">
        <div style={{ display: 'inline-block', marginBottom: '8px' }}>
          <img 
            src={LOGO_LIGHT_BG} 
            alt="KR Enterprise Group — Health & Life Insurance Services" 
            className="login-logo"
            style={{ height: '96px', width: 'auto', maxWidth: '360px', objectFit: 'contain', margin: 0 }}
          />
        </div>
        
        <h1 className="login-title font-heading">
          {mode === 'activate' ? 'Activate Your Account' : 'KR Agency Hub'}
        </h1>
        <p className="login-subtitle">
          {mode === 'activate' 
            ? 'Set your password to complete your account setup'
            : 'Agent & Agency Management Portal'
          }
        </p>
        
        <div
          className="mb-6"
          translate="no"
          style={{ display: error ? 'block' : 'none' }}
          data-testid="login-error"
        >
          <Alert variant="destructive" className="notranslate">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        </div>
        
        {twoFA ? (
          <div data-testid="twofa-panel">
            <div className="flex items-center gap-2 mb-4 text-slate-700">
              <ShieldCheck size={18} className="text-sky-700" />
              <span className="font-semibold">Two-step verification</span>
            </div>

            {twoFA.stage === 'choose' && (
              <div className="space-y-3">
                <p className="text-sm text-slate-500">Choose where to send your one-time code.</p>
                <button
                  type="button"
                  onClick={() => chooseChannel('email')}
                  disabled={loading}
                  className="w-full flex items-center gap-3 border rounded-lg p-3 text-left hover:border-sky-500 transition-colors disabled:opacity-50"
                  data-testid="twofa-channel-email"
                >
                  <Mail size={18} className="text-slate-500" />
                  <span className="flex-1">
                    <span className="block text-sm font-medium text-slate-800">Email</span>
                    <span className="block text-xs text-slate-500">{twoFA.email_masked}</span>
                  </span>
                </button>
                {(twoFA.channels || []).includes('sms') && (
                  <button
                    type="button"
                    onClick={() => chooseChannel('sms')}
                    disabled={loading}
                    className="w-full flex items-center gap-3 border rounded-lg p-3 text-left hover:border-sky-500 transition-colors disabled:opacity-50"
                    data-testid="twofa-channel-sms"
                  >
                    <MessageSquare size={18} className="text-slate-500" />
                    <span className="flex-1">
                      <span className="block text-sm font-medium text-slate-800">Text message (SMS)</span>
                      <span className="block text-xs text-slate-500">{twoFA.phone_masked}</span>
                    </span>
                  </button>
                )}
                <button type="button" onClick={resetTwoFactor} className="text-xs text-slate-500 hover:text-slate-700" data-testid="twofa-back">← Back to sign in</button>
              </div>
            )}

            {twoFA.stage === 'code' && (
              <form onSubmit={verifyTwoFactor} className="space-y-4">
                <p className="text-sm text-slate-500">
                  We sent a 6-digit code via {twoFA.channel === 'sms' ? 'SMS' : 'email'} to <strong>{twoFA.destination}</strong>. Enter it below.
                </p>
                <Input
                  autoFocus
                  inputMode="numeric"
                  maxLength={6}
                  placeholder="000000"
                  value={twoFACode}
                  onChange={(e) => setTwoFACode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  className="text-center text-2xl tracking-[0.4em] font-semibold"
                  data-testid="twofa-code-input"
                />
                <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
                  <input type="checkbox" checked={rememberDevice} onChange={(e) => setRememberDevice(e.target.checked)} className="accent-sky-700" data-testid="twofa-remember" />
                  Trust this device for 30 days
                </label>
                <Button type="submit" className="w-full" disabled={loading || twoFACode.length < 6} data-testid="twofa-verify-btn">
                  {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Verify & sign in
                </Button>
                <div className="flex items-center justify-between text-xs">
                  <button type="button" onClick={() => chooseChannel(twoFA.channel)} className="text-sky-700 hover:text-sky-900" disabled={loading} data-testid="twofa-resend">Resend code</button>
                  <button type="button" onClick={() => setTwoFA((p) => ({ ...p, stage: 'choose' }))} className="text-slate-500 hover:text-slate-700">Use another method</button>
                </div>
              </form>
            )}
          </div>
        ) : (
        <form onSubmit={handleSubmit} className="space-y-5">
          <div className="space-y-2">
            <Label htmlFor="email">Email Address</Label>
            <Input
              id="email"
              name="email"
              type="email"
              placeholder="you@example.com"
              value={formData.email}
              onChange={handleChange}
              disabled={mode === 'activate'}
              required
              data-testid="email-input"
            />
          </div>
          
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="password">Password</Label>
              {mode === 'login' && (
                <Link to="/forgot-password" className="text-xs text-sky-700 hover:text-sky-900" data-testid="login-forgot-link">
                  Forgot password?
                </Link>
              )}
            </div>
            <PasswordInput
              id="password"
              name="password"
              placeholder="••••••••"
              value={formData.password}
              onChange={handleChange}
              required
              data-testid="password-input"
            />
          </div>
          
          {mode === 'activate' && (
            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Confirm Password</Label>
              <PasswordInput
                id="confirmPassword"
                name="confirmPassword"
                placeholder="••••••••"
                value={formData.confirmPassword}
                onChange={handleChange}
                required
                data-testid="confirm-password-input"
              />
            </div>
          )}
          
          <Button 
            type="submit" 
            className="w-full"
            disabled={loading}
            data-testid="submit-btn"
          >
            {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {mode === 'activate' ? 'Activate Account' : 'Sign In'}
          </Button>
        </form>
        )}
        
        {mode === 'login' && (
          <p className="mt-6 text-sm text-center text-slate-500">
            Don't have an account? Contact your agency administrator for an invitation.
          </p>
        )}
        <div className="mt-4 flex items-center justify-center gap-3 text-xs text-slate-400">
          <Link to="/terms" className="hover:text-slate-600 transition-colors" data-testid="login-terms-link">Terms & Conditions</Link>
          <span>|</span>
          <Link to="/privacy" className="hover:text-slate-600 transition-colors" data-testid="login-privacy-link">Privacy Policy</Link>
          <span>|</span>
          <Link to="/refund-policy" className="hover:text-slate-600 transition-colors" data-testid="login-refund-link">Refund Policy</Link>
        </div>
      </div>
    </div>
  );
}
